import { FC } from 'react';
import { Award, Brain, Trophy, ChevronRight, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAchievements } from '@/hooks/useAchievements';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { useProfile } from '@/hooks/useProfile';
import { useBadges } from '@/hooks/useBadges';
import { GlobalRanking } from '@/components/achievements/GlobalRanking';
import { MedalsCollection } from '@/components/achievements/MedalsCollection';

const AchievementsPage: FC = () => {
  const { user, loading } = useAuth();
  const { getAchievements } = useAchievements();
  const { userProfile, isLoading: isLoadingProfile } = useProfile();
  const { data: badges, isLoading: isLoadingBadges } = useBadges();

  const { data: achievements, isLoading: isLoadingAchievements } = useQuery({
    queryKey: ['achievements', user?.id],
    queryFn: () => getAchievements(user!.id),
    enabled: !!user?.id,
  });

  // Función para calcular el progreso al siguiente nivel
  const calculateNextLevelProgress = () => {
    if (!userProfile?.cerebrix) {
      return { currentLevel: 1, currentPoints: 0, progress: 0, remainingPoints: 100 };
    }
    
    const currentPoints = userProfile.cerebrix;
    const currentLevel = Math.ceil(currentPoints / 100);
    const nextLevelPoints = currentLevel * 100;
    const basePoints = (currentLevel - 1) * 100;
    const progress = ((currentPoints - basePoints) / (nextLevelPoints - basePoints)) * 100;
    const remainingPoints = nextLevelPoints - currentPoints;
    
    console.log('Calculando progreso en Achievements:', {
      currentPoints,
      currentLevel,
      nextLevelPoints,
      remainingPoints,
      progress,
      userProfile: {
        cerebrix: userProfile.cerebrix,
        level: userProfile.level
      }
    });

    return {
      currentLevel,
      currentPoints,
      progress,
      remainingPoints
    };
  };

  // Función para obtener el badge actual y el siguiente
  const getBadgeInfo = () => {
    if (!badges || !userProfile?.level) {
      return {
        currentBadge: { badge_name: 'Aprendiz Financiero', badge_icon: '🔰' },
        nextBadge: { badge_name: 'Ahorrador Novato', badge_icon: '🐣' }
      };
    }

    const currentBadge = badges.find(b => b.level === userProfile.level) || badges[0];
    const nextBadge = badges.find(b => b.level === (userProfile.level + 1)) || currentBadge;

    return { currentBadge, nextBadge };
  };

  if (loading || isLoadingProfile || isLoadingAchievements || isLoadingBadges) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user || !userProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">No se encontró el perfil</h2>
            <p className="text-muted-foreground">Por favor, inicia sesión para ver tus logros.</p>
          </div>
        </div>
      </div>
    );
  }

  const { currentBadge, nextBadge } = getBadgeInfo();
  const { progress, remainingPoints } = calculateNextLevelProgress();

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4 py-8">
        {/* Título */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-8"
        >
          <Trophy className="w-8 h-8 text-primary animate-pulse" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Mis Logros y Progreso
          </h1>
        </motion.div>

        {/* Nivel Actual y Próximo Nivel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-lg border border-primary/10 mt-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Nivel Actual */}
            <div className="text-center space-y-4">
              <div className="inline-block p-4 rounded-full bg-gradient-to-br from-primary/10 to-secondary/10">
                <div className="text-6xl">{currentBadge.badge_icon}</div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-primary">Nivel Actual</h3>
                <p className="text-xl text-foreground">{currentBadge.badge_name}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  ¡Tienes {userProfile.cerebrix} Cerebrix acumulados! 🌟
                </p>
              </div>
            </div>

            {/* Flecha y Próximo Nivel */}
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-4">
                <ChevronRight className="w-8 h-8 text-primary animate-pulse" />
                <div className="inline-block p-4 rounded-full bg-gradient-to-br from-secondary/10 to-primary/10 opacity-70">
                  <div className="text-6xl opacity-70">{nextBadge.badge_icon}</div>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-secondary">Próximo Nivel</h3>
                <p className="text-xl text-foreground opacity-70">{nextBadge.badge_name}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  ¡Te faltan solo <span className="font-bold text-secondary">{remainingPoints}</span> Cerebrix!
                </p>
              </div>
            </div>
          </div>

          {/* Barra de Progreso */}
          <div className="mt-6 space-y-2">
            <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 1, delay: 0.5 }}
                className="h-full bg-gradient-to-r from-primary via-secondary to-accent rounded-full"
              />
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>¡{progress.toFixed(0)}% del camino!</span>
              <span>Solo {(100 - progress).toFixed(0)}% para el siguiente nivel</span>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <StatsCard
            title="Cerebrix por Lecciones"
            value={userProfile.lesson_cerebrix || 0}
            icon={Brain}
            description="Ganados completando lecciones"
          />
          <StatsCard
            title="Cerebrix por Juegos"
            value={userProfile.game_cerebrix || 0}
            icon={Star}
            description="Ganados jugando minijuegos"
          />
          <StatsCard
            title="Cerebrix Totales"
            value={userProfile.cerebrix || 0}
            icon={Award}
            description="Tu progreso como financiero"
          />
        </div>

        {/* Ranking Global y Medallas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <GlobalRanking />
          <MedalsCollection achievements={achievements || []} />
        </div>
      </div>
    </div>
  );
};

interface StatsCardProps {
  title: string;
  value: number;
  icon: any;
  description: string;
}

const StatsCard: FC<StatsCardProps> = ({
  title,
  value,
  icon: Icon,
  description,
}) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
    className="bg-white p-6 rounded-xl shadow-sm border border-border hover:shadow-md transition-shadow"
  >
    <div className="flex items-center gap-4">
      <div className="p-2 bg-primary/10 rounded-lg">
        <Icon className="w-8 h-8 text-primary" />
      </div>

      <div>
        <h3 className="font-semibold text-foreground">{title}</h3>
        <p className="text-2xl font-bold text-primary">{value}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  </motion.div>
);

export default AchievementsPage;